import { useField, useFormikContext } from 'formik';
import * as React from 'react';
import { MutableRefObject, ReactElement, useEffect, useRef, useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { useDebounce } from 'react-use';
import { Box, Flex } from 'reflexbox';
import styled, { css } from 'styled-components';

import * as graphql from 'generated/graphql';
import CloseIcon from 'packages/kakapo-ui-kit/assets/icons/CloseIcon';
import { ParagraphSmall } from 'packages/kakapo-ui-kit/components/common/Paragraph';
import Field, {
	FieldType,
	InputElement,
} from 'packages/kakapo-ui-kit/components/forms/controls/Field';
import Select from 'packages/kakapo-ui-kit/components/forms/controls/Select';
import AdjustableProgressBar from 'packages/kakapo-ui-kit/components/progress-indicator/AdjustableProgressBar';
import useOutsideClick from 'packages/kakapo-ui-kit/helpers/UseOutsideClick';
import { IOption } from 'packages/kakapo-ui-services/types/Option';

import { Input } from '@treams/treams-ui';
import Multilingual from '../../forms/controls/Multilingual';
import MultilingualQuill from '../../forms/controls/MultilingualQuill';
import Toggle from '../../forms/controls/Toggle';

const Wrapper = styled.div`
	width: 100%;

	input {
		width: 100%;
	}
`;

export const Value = styled(ParagraphSmall)`
	display: flex;
	align-items: center;
	padding: 8px 19px;
	line-height: 30px;
	margin: 0;
	font-size: 16px;
	cursor: pointer;
	border: 1px solid transparent;
	transition: 150ms ease-out border-color;
	border-radius: 4px;

	&:hover {
		border-color: ${(props) => props.theme.colors.lightBlueLight};
	}
`;

type ValueWrapperProps = {
	focused?: boolean;
};

const ValueWrapper = styled.div<ValueWrapperProps>`
	// Display none for all Field values
	> div,
	> input {
		display: none;
	}

	span {
		display: block;
	}

	${(props) =>
		props.focused &&
		css`
			> div,
			> input {
				display: block;
			}

			span {
				display: none;
			}
		`}
`;

export enum DetailsTableFormValueTypes {
	Date,
	Text,
	TextMultiline,
	Custom,
	ProgressBar,
	// eslint-disable-next-line @typescript-eslint/no-shadow
	Multilingual,
	MultiSelect,
	MultilingualQuill,
	// eslint-disable-next-line @typescript-eslint/no-shadow
	Number,
	Select,
	Switch,
	ColorPicker,
}

interface NumberProps {
	initialValue?: number;
	name: string;
	onEditingForm?: (isEditing: boolean) => void;
	onChange?: (numberValue?: number) => void;
	type: DetailsTableFormValueTypes;
	alwaysFocused?: boolean;
}

function NumberValue({
	initialValue,
	onEditingForm = () => {},
	name,
	onChange = () => {},
	type,
	alwaysFocused = false,
}: NumberProps) {
	const [isFocused, setIsFocused] = useState(false);
	const [numberValue, setNumberValue] = useState(initialValue);
	const formik = useFormikContext();

	useEffect(() => onChange(numberValue), [numberValue]);

	function handleClick() {
		setIsFocused(true);
		// TODO: Handle state to SettingsCreateNewTeam to display that you're edting the form.
		// onEditingForm(true);
	}

	function toNumberValue(value: string): number | undefined {
		if (value !== '') {
			return parseInt(value, 10);
		}
		return undefined;
	}

	function onBlur(event?: React.ChangeEvent<HTMLInputElement>) {
		setIsFocused(false);

		if (event) {
			setNumberValue(toNumberValue(event.target.value));
		}
	}

	return (
		<ValueWrapper focused={isFocused || alwaysFocused}>
			<span onClick={handleClick} onKeyUp={handleClick} role="button" tabIndex={0}>
				<Value color="slateBlue">
					{numberValue !== null && numberValue !== undefined && typeof numberValue === 'number'
						? numberValue.toFixed()
						: '—'}
				</Value>
			</span>

			{(isFocused || alwaysFocused) && (
				<Field
					autoFocus
					type={FieldType.Number}
					placeholder="—"
					name={name}
					onBlur={onBlur}
					onChange={(event) =>
						formik.handleChange(event.target.name)({
							target: {
								value: toNumberValue(event.target.value),
							},
						} as any)
					}
					fullWidth
					resizable={type === DetailsTableFormValueTypes.TextMultiline}
					unspaced
					hideError
				/>
			)}
		</ValueWrapper>
	);
}

interface TextProps {
	initialValue?: string;
	name: string;
	onEditingForm?: (isEditing: boolean) => void;
	onChange?: (textValue?: string) => void;
	type: DetailsTableFormValueTypes;
	alwaysFocused?: boolean;
}

function TextValue({
	initialValue,
	onEditingForm = () => {},
	name,
	onChange = () => {},
	type,
	alwaysFocused = false,
}: TextProps) {
	const [isFocused, setIsFocused] = useState(false);
	const [textValue, setTextValue] = useState(initialValue);

	useEffect(() => onChange(textValue), [textValue]);

	function handleClick() {
		setIsFocused(true);
		// TODO: Handle state to SettingsCreateNewTeam to display that you're edting the form.
		// onEditingForm(true);
	}

	function onBlur(event?: React.ChangeEvent<HTMLInputElement>) {
		setIsFocused(false);
		// TODO: Handle state to SettingsCreateNewTeam to display that you're edting the form.
		// onEditingForm(false);

		if (event) {
			if (event.target.value !== '') {
				setTextValue(event.target.value);
			} else {
				setTextValue('');
			}
		}
	}

	return (
		<ValueWrapper focused={isFocused || alwaysFocused}>
			<span onClick={handleClick} onKeyUp={handleClick} role="button" tabIndex={0}>
				<Value color="slateBlue">{textValue || '—'}</Value>
			</span>

			{(isFocused || alwaysFocused) && (
				<Field
					autoFocus
					type={FieldType.Text}
					placeholder="—"
					name={name}
					onBlur={onBlur}
					fullWidth
					resizable={type === DetailsTableFormValueTypes.TextMultiline}
					unspaced
					hideError
				/>
			)}
		</ValueWrapper>
	);
}

function DateValue({ name }: { name: string }) {
	return <Input.DatePicker.Formik name={name} />;
}

export interface IOptions {
	label: string;
	value: string;
	color?: string;
}

interface IOvalProps {
	color?: string;
}

const Oval = styled.div<IOvalProps>`
	background-color: ${({ color }) => color};
	border-radius: 50%;
	height: 12px;
	width: 12px;
	margin: 0 8px 0 0;
`;

interface ISelectProps {
	initialOption?: IOptions;
	options?: IOptions[];
	name: string;
}

const ClearButton = styled.div`
	color: ${(props) => props.theme.colors.primary};
	background: rgba(0, 0, 0, 0.1);
	border-radius: 12px;
	width: 24px;
	height: 24px;
	display: flex;
	align-items: center;
	justify-content: center;

	svg {
		display: block;
		width: 8px;
		height: 8px;
	}
`;

function SelectValue({ initialOption, options, name }: ISelectProps) {
	const [isFocused, setIsFocused] = useState(false);
	const [field, , { setValue }] = useField<string | undefined>(name);
	const targetRef = useRef() as MutableRefObject<HTMLInputElement>;

	useOutsideClick(targetRef, () => setIsFocused(false));

	const handleClick = () => {
		setIsFocused(true);
	};

	const selected = options?.find((option) => option.value === field.value);

	return (
		<ValueWrapper focused={isFocused}>
			<span onClick={handleClick} onKeyUp={handleClick} role="button" tabIndex={0}>
				<Value color="slateBlue">
					{selected?.color && <Oval color={selected.color} />}
					{selected?.label || '—'}
					{selected && (
						<>
							<span style={{ flexGrow: 1 }} />
							<ClearButton
								onClick={(event) => {
									setValue(undefined);
									event.preventDefault();
									event.stopPropagation();
								}}
							>
								<CloseIcon />
							</ClearButton>
						</>
					)}
				</Value>
			</span>

			<div ref={targetRef}>
				<Select
					options={options}
					unspaced
					onChange={(option) => {
						setValue(option!.value);
						setIsFocused(false);
					}}
					value={selected}
					defaultValue={initialOption}
					closeMenuOnSelect
					blurInputOnSelect
					isSearchable={false}
					autoFocus={isFocused}
					menuIsOpen={isFocused}
				/>
			</div>
		</ValueWrapper>
	);
}

function MultiSelectValue({ initialOption, options, name }: ISelectProps) {
	const [isFocused, setIsFocused] = useState(false);
	const [field, , { setValue }] = useField<string[] | undefined>(name);
	const targetRef = useRef() as MutableRefObject<HTMLInputElement>;

	useOutsideClick(targetRef, () => setIsFocused(false));

	const handleClick = () => {
		setIsFocused(true);
	};

	const selected = options?.filter((option) => field.value?.includes(option.value));

	return (
		<ValueWrapper focused={isFocused}>
			<span onClick={handleClick} onKeyUp={handleClick} role="button" tabIndex={0}>
				<Value color="slateBlue">
					{selected && selected.length > 0
						? selected.map((option) => option.label).join(', ')
						: '—'}
				</Value>
			</span>

			<div ref={targetRef}>
				<Select
					options={options}
					unspaced
					onChange={(newOptions: any) => {
						setValue(newOptions?.map((option: IOption<void>) => option.value));
					}}
					value={selected as any}
					defaultValue={initialOption}
					closeMenuOnSelect
					blurInputOnSelect
					isSearchable
					multi
					autoFocus={isFocused}
					menuIsOpen={isFocused}
				/>
			</div>
		</ValueWrapper>
	);
}

const ProgressValueLabel = styled.span`
	color: ${(props) => props.theme.colors.slateBlue};
`;

function ProgressValue({ name }: { name: string }) {
	const [field, , { setValue }] = useField<graphql.GoalChecklistItemProgress>(name);

	return (
		<Flex mx={19}>
			<ProgressValueLabel>0%</ProgressValueLabel>
			<Box flexGrow={1} mx={2}>
				<AdjustableProgressBar
					initialProgress={field.value.value}
					onChange={(progress) => {
						setValue({
							value: progress,
						} as any);
					}}
				/>
			</Box>
			<ProgressValueLabel>100%</ProgressValueLabel>
		</Flex>
	);
}

interface PlainTextProps {
	value?: string;
	name: string;
	onChange?: (textValue?: string) => void;
	alwaysFocused?: boolean;
}

function PlainTextValue({ value, name, onChange, alwaysFocused }: PlainTextProps) {
	const [isFocused, setIsFocused] = useState(false);

	function handleClick() {
		setIsFocused(true);
	}

	function onBlur(event?: React.ChangeEvent<HTMLInputElement>) {
		setIsFocused(false);

		if (event) {
			if (event.target.value !== '') {
				onChange?.(event.target.value);
			} else {
				onChange?.('');
			}
		}
	}

	return (
		<ValueWrapper focused={isFocused || alwaysFocused}>
			<span onClick={handleClick} onKeyUp={handleClick} role="button" tabIndex={0}>
				<Value color="slateBlue">{value || '—'}</Value>
			</span>

			{(isFocused || alwaysFocused) && (
				<InputElement
					autoFocus
					value={value}
					onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
						onChange?.(event.target.value);
					}}
					type={FieldType.Text}
					placeholder="—"
					name={name}
					onBlur={onBlur}
					fullWidth
				/>
			)}
		</ValueWrapper>
	);
}

interface ColorPickerProps {
	value?: string;
	onChange?: (value?: string) => void;
	name: string;
	type: DetailsTableFormValueTypes;
}

function ColorValue({ value, onChange = () => {}, name, type }: ColorPickerProps) {
	const [colorValue, setColorValue] = useState<string | undefined>(value);

	useDebounce(
		() => {
			onChange(colorValue);
		},
		100,
		[colorValue],
	);

	return (
		<>
			<HexColorPicker color={value} onChange={setColorValue} />

			<Box mt={2}>
				<PlainTextValue value={value} onChange={onChange} name={name} />
			</Box>
		</>
	);
}

interface IProps {
	initialOption?: IOptions;
	initialValue?: string;
	options?: IOptions[];
	alwaysFocused?: boolean;
	name: string;
	onEditingForm?: (isEditing: boolean) => void;
	type: DetailsTableFormValueTypes;
	onClick?: () => void | undefined;
}

export default function DetailsTableFormValue({
	initialValue,
	initialOption,
	options,
	onEditingForm,
	name,
	type,
	onClick,
	alwaysFocused,
}: IProps): ReactElement {
	const [field, , helpers] = useField(name);

	return (
		<Wrapper>
			{type === DetailsTableFormValueTypes.Text && (
				<TextValue
					initialValue={field.value}
					name={name}
					type={type}
					alwaysFocused={alwaysFocused}
				/>
			)}

			{type === DetailsTableFormValueTypes.Number && (
				<NumberValue
					initialValue={field.value}
					name={name}
					type={type}
					alwaysFocused={alwaysFocused}
				/>
			)}

			{type === DetailsTableFormValueTypes.TextMultiline && (
				<TextValue initialValue={field.value} name={name} type={type} />
			)}

			{type === DetailsTableFormValueTypes.Date && <DateValue name={name} />}

			{type === DetailsTableFormValueTypes.Custom && (
				<span onClick={onClick} onKeyUp={onClick} role="button" tabIndex={0}>
					<Value color="slateBlue">{initialValue}</Value>
				</span>
			)}

			{type === DetailsTableFormValueTypes.Multilingual && <Multilingual name={name} />}

			{type === DetailsTableFormValueTypes.MultilingualQuill && <MultilingualQuill name={name} />}

			{type === DetailsTableFormValueTypes.ProgressBar && <ProgressValue name={name} />}

			{type === DetailsTableFormValueTypes.Select && (
				<SelectValue options={options} initialOption={initialOption} name={name} />
			)}

			{type === DetailsTableFormValueTypes.MultiSelect && (
				<MultiSelectValue options={options} initialOption={initialOption} name={name} />
			)}

			{type === DetailsTableFormValueTypes.Switch && (
				<Toggle
					checked={field.value}
					onChange={() => helpers.setValue(!field.value)}
					value={name}
					name={name}
					unspaced
				/>
			)}

			{type === DetailsTableFormValueTypes.ColorPicker && (
				<ColorValue
					value={field.value}
					onChange={(value) => helpers.setValue(value)}
					name={name}
					type={type}
				/>
			)}
		</Wrapper>
	);
}
